import { Link } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';
import { Skeleton, cn } from '@blimo/ui';

/**
 * KPI card: label 13 px muted (+ icon), value 24 px semibold with tabular digits, hint 12 px.
 * Optionally the whole card links somewhere (stretched link with screen-reader text).
 * `value` must come from the API – never hardcode business figures.
 * @param {{
 *   label: import('react').ReactNode,
 *   value?: import('react').ReactNode,
 *   hint?: import('react').ReactNode,
 *   icon?: import('react').ComponentType<any>,
 *   to?: string,
 *   linkText?: string,
 *   loading?: boolean,
 *   emptyText?: import('react').ReactNode,
 *   className?: string,
 *   children?: import('react').ReactNode,
 * }} props
 *   children: replaces the value (e.g. badges)
 */
export function KpiCard({ label, value, hint, icon: Icon, to, linkText, loading = false, emptyText = 'Noch keine Daten', className, children }) {
  const hasValue = value !== null && value !== undefined && value !== '';
  return (
    <div
      data-slot="card"
      className={cn(
        'group/kpi relative flex min-w-0 flex-col gap-1.5 rounded-xl border bg-card px-4 py-3.5',
        to && 'transition-colors hover:bg-muted/40',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 text-[0.8125rem] font-medium leading-5 text-muted-foreground">{label}</span>
        {to ? (
          <ArrowUpRight className="mt-0.5 size-4 shrink-0 text-muted-foreground/70 transition-colors group-hover/kpi:text-foreground" aria-hidden="true" />
        ) : (
          Icon && <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground/70" aria-hidden="true" />
        )}
      </div>
      {children ??
        (loading ? (
          <Skeleton className="h-7 w-20" />
        ) : hasValue ? (
          <div className="truncate text-xl font-semibold leading-8 tracking-tight tabular-nums sm:text-2xl">{value}</div>
        ) : (
          <div className="text-[0.8125rem] leading-8 text-muted-foreground">{emptyText}</div>
        ))}
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      {to && (
        <Link to={to} className="stretched-link">
          <span className="sr-only">{linkText}</span>
        </Link>
      )}
    </div>
  );
}
